import { Outlet, Link, useLocation, useParams } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useCompanyLogo } from '@/hooks/useCompanyLogo';
import ClientBottomNav from './ClientBottomNav';
import type { PortalCustomization } from '../../hooks/usePortalCustomization';

interface ClientPortalMobileLayoutProps {
  clientName: string;
  handleExit: () => void;
  customization?: PortalCustomization;
}

const ACCENT_COLOR_MAP: Record<string, string> = {
  pink: '#ec4899',
  rose: '#f43f5e',
  amber: '#d97706',
  violet: '#8b5cf6',
  emerald: '#059669',
  sky: '#0284c7',
  fuchsia: '#c026d3',
};

const DEFAULT_ACCENT = '#0d9488';

export default function ClientPortalMobileLayout({ clientName, handleExit, customization }: ClientPortalMobileLayoutProps) {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const companyLogo = useCompanyLogo();
  const [scrolled, setScrolled] = useState(false);

  const accentColor = useMemo(() => {
    if (customization?.accentColor && ACCENT_COLOR_MAP[customization.accentColor]) {
      return ACCENT_COLOR_MAP[customization.accentColor];
    }
    return DEFAULT_ACCENT;
  }, [customization?.accentColor]);

  const bgImage = customization?.backgroundUrl || null;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="h-[100dvh] w-full flex flex-col overflow-hidden relative">
      {/* Fondo personalizado */}
      {bgImage && (
        <>
          <div
            className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${bgImage})` }}
          ></div>
          <div className="fixed inset-0 z-[1] bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </>
      )}

      {/* Contenido */}
      <div className="relative z-10 h-full w-full flex flex-col">
        {/* Header */}
        <header
          className={`flex-shrink-0 z-40 transition-all duration-300 ${
            scrolled
              ? bgImage
                ? 'bg-black/40 backdrop-blur-md shadow-sm border-b border-white/20'
                : 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100'
              : bgImage
                ? 'bg-transparent border-b border-transparent'
                : 'bg-white border-b border-transparent'
          }`}
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl ${bgImage ? 'bg-white/10' : 'bg-gray-50'}`}>
                <img
                  src={companyLogo || 'https://fotosyvideosantarosa.com/storage/2025/12/fyvnueva2.png'}
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="min-w-0">
                <p className={`text-sm font-bold truncate leading-tight ${bgImage ? 'text-white' : 'text-gray-900'}`}>{clientName}</p>
                <p className={`text-xs leading-tight ${bgImage ? 'text-white/60' : 'text-gray-400'}`}>Portal Cliente</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <a
                href="https://wa.me/17078066168"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors cursor-pointer ${
                  bgImage ? 'text-green-400 hover:bg-white/10' : 'text-green-600 hover:bg-green-50'
                }`}
                aria-label="WhatsApp"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-xl"></i>
                </div>
              </a>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main
          className="flex-1 overflow-y-auto"
          style={{ paddingBottom: 'calc(64px + env(safe-area-inset-bottom, 0px) + 8px)' }}
        >
          <Outlet />
        </main>

        {/* Bottom Nav */}
        <ClientBottomNav clientName={clientName} handleExit={handleExit} bgImage={bgImage} accentColor={accentColor} />
      </div>
    </div>
  );
}